import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { atPacketAPI } from '../services/api';
import { hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import PageHeader from '../components/PageHeader';
import './AtPackets.css';

const SORT = {
  item_type: 'item_type',
  item_name: 'item_name',
  client: 'client',
  sc: 'sc',
  latest_request_date: 'latest_request_date',
  packet_status: 'packet_status',
  comment: 'comment',
};

function formatSc(lastName, firstName) {
  const parts = [lastName, firstName].filter(Boolean);
  return parts.length ? parts.join(', ') : '—';
}

function cmpStr(av, bv, dir) {
  const a = (av ?? '').toString().toLowerCase();
  const b = (bv ?? '').toString().toLowerCase();
  if (a < b) return dir === 'asc' ? -1 : 1;
  if (a > b) return dir === 'asc' ? 1 : -1;
  return 0;
}

/** Null / invalid dates sort last */
function cmpDate(aVal, bVal, dir) {
  const ta = aVal ? new Date(aVal).getTime() : NaN;
  const tb = bVal ? new Date(bVal).getTime() : NaN;
  const aBad = Number.isNaN(ta);
  const bBad = Number.isNaN(tb);
  if (aBad && bBad) return 0;
  if (aBad) return 1;
  if (bBad) return -1;
  if (ta < tb) return dir === 'asc' ? -1 : 1;
  if (ta > tb) return dir === 'asc' ? 1 : -1;
  return 0;
}

function compareRows(a, b, sortKey, sortDir) {
  switch (sortKey) {
    case SORT.item_type:
      return cmpStr(
        `${a.item_type_display_name || ''} ${a.item_type_code || ''}`.trim(),
        `${b.item_type_display_name || ''} ${b.item_type_code || ''}`.trim(),
        sortDir
      );
    case SORT.item_name:
      return cmpStr(a.item_name_label, b.item_name_label, sortDir);
    case SORT.client:
      return cmpStr(
        `${a.client_last_name || ''} ${a.client_first_name || ''}`.trim(),
        `${b.client_last_name || ''} ${b.client_first_name || ''}`.trim(),
        sortDir
      );
    case SORT.sc:
      return cmpStr(
        `${a.sc_last_name || ''} ${a.sc_first_name || ''}`.trim(),
        `${b.sc_last_name || ''} ${b.sc_first_name || ''}`.trim(),
        sortDir
      );
    case SORT.latest_request_date:
      return cmpDate(a.latest_request_date, b.latest_request_date, sortDir);
    case SORT.packet_status:
      return cmpStr(
        `${a.packet_status_display_name || ''} ${a.packet_status_code || ''}`.trim(),
        `${b.packet_status_display_name || ''} ${b.packet_status_code || ''}`.trim(),
        sortDir
      );
    case SORT.comment:
      return cmpStr(a.item_comment, b.item_comment, sortDir);
    default:
      return 0;
  }
}

/**
 * Replaces legacy Access "View AT Delay Item query" / AT_delay_item_form: items awaiting documentation receipt.
 */
function AtPacketsPendingReceipt() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const clientIdFilter = searchParams.get('clientId') || '';
  const { tenantPath } = useTenantFromUrl();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [sortKey, setSortKey] = useState(SORT.latest_request_date);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hasPermission('at_packets.read')) {
        setError('You do not have permission to view AT packets.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const params = {};
        if (clientIdFilter) params.clientId = clientIdFilter;
        const res = await atPacketAPI.listPendingReceiptItems(params);
        if (!cancelled) setRows(res.data?.data ?? []);
      } catch (err) {
        if (!cancelled) {
          setRows([]);
          setError(err.response?.data?.error || err.message || 'Failed to load');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [clientIdFilter]);

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => compareRows(a, b, sortKey, sortDir));
    return copy;
  }, [rows, sortKey, sortDir]);

  const handleSortClick = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sortMark = (key) => (sortKey === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : '');

  const thSortable = (key, labelEl, title) => (
    <th
      scope="col"
      className="at-packets-pending-th-sortable"
      onClick={() => handleSortClick(key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSortClick(key);
        }
      }}
      title={title || undefined}
      tabIndex={0}
      role="columnheader"
      aria-sort={sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <div className="at-packets-pending-th-cell">
        <div className="at-packets-pending-th-label">{labelEl}</div>
        <span className="at-packets-pending-sort-indicator" aria-hidden="true">{sortMark(key)}</span>
      </div>
    </th>
  );

  if (!hasPermission('at_packets.read')) {
    return (
      <div className="at-packets-list-page at-packets-pending-receipt-page">
        <p className="at-packets-error">You do not have permission to view this report.</p>
        <Link to={tenantPath('/lists/at-packets')}>← Back to AT Packets</Link>
      </div>
    );
  }

  return (
    <div className="at-packets-list-page at-packets-pending-receipt-page">
      <div className="header">
        <PageHeader
          title={(
            <span className="at-packets-pending-title-with-info">
              AT Docs – Pending Receipt
              <button
                type="button"
                className="at-packets-pending-info-icon"
                onClick={() => setShowInfo((v) => !v)}
                aria-expanded={showInfo}
                aria-controls="at-docs-pending-info"
                aria-label={showInfo ? 'Hide explanation' : 'Show explanation'}
              >
                ⓘ
              </button>
            </span>
          )}
        />
      </div>
      {showInfo && (
        <p id="at-docs-pending-info" className="at-packets-pending-info-text">
          Line items with a <strong>latest request date</strong> that still need a <strong>received</strong> date (excludes items marked &quot;dates not required&quot;).
          Only clients with an <strong>open current referral</strong> (not closed), like legacy &quot;closing date&quot; null.
          AT packet must have a <strong>status</strong> (blank status is excluded). Packets in these statuses are <strong>excluded</strong>{' '}
          (legacy ATStatusCheck true): <strong>Trialing</strong>, <strong>Approved</strong>, <strong>Partially approved</strong>,{' '}
          <strong>Denied</strong>, <strong>Family fee delinquent</strong>. All other packet statuses are included.
          Matches the legacy &quot;AT delay item&quot; documentation queue.
          <strong> SC</strong> is the service coordinator assigned to the client.
        </p>
      )}

      {error && <p className="at-packets-error">{error}</p>}

      <div className="at-packets-table at-packets-pending-receipt-table">
        <table>
          <thead>
            <tr>
              {thSortable(SORT.item_type, 'Item type')}
              {thSortable(SORT.item_name, 'Item name')}
              {thSortable(SORT.client, 'Client')}
              {thSortable(SORT.sc, 'SC', 'Service coordinator assigned to client')}
              {thSortable(
                SORT.latest_request_date,
                <span className="at-packets-pending-th-stack">
                  <span>Latest request</span>
                  <span>date</span>
                </span>,
                'Latest request date'
              )}
              {thSortable(
                SORT.packet_status,
                <span className="at-packets-pending-th-stack">
                  <span>Packet</span>
                  <span>status</span>
                </span>,
                'Packet status'
              )}
              {thSortable(SORT.comment, 'Comment')}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="no-data loading-cell">Loading…</td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={7} className="no-data">No pending receipt items.</td>
              </tr>
            ) : (
              sortedRows.map((r) => (
                <tr
                  key={r.item_id}
                  className="clickable-row"
                  onClick={() => navigate(tenantPath(`/lists/at-packets/${r.at_packet_id}`))}
                  title="Open AT packet"
                >
                  <td>{r.item_type_display_name || r.item_type_code || '—'}</td>
                  <td>{r.item_name_label || '—'}</td>
                  <td>{[r.client_last_name, r.client_first_name].filter(Boolean).join(', ') || '—'}</td>
                  <td>{formatSc(r.sc_last_name, r.sc_first_name)}</td>
                  <td>{r.latest_request_date ? formatDisplayDate(r.latest_request_date) : '—'}</td>
                  <td>{r.packet_status_display_name || r.packet_status_code || '—'}</td>
                  <td className="at-packets-pending-comment">{r.item_comment || ''}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AtPacketsPendingReceipt;
